// src/components/Navigation.js
import React, { useState } from 'react';
import { Sparkles, Mic, ChevronDown, Music, FileAudio, Repeat, Wind, MessageSquare, X, Menu } from 'lucide-react';

const Navigation = ({ activeTool, setActiveTool }) => {
  const [openMenu, setOpenMenu] = useState(null); // 'audio' | 'video' | null
  const [mobileOpen, setMobileOpen] = useState(false);

  const audioTools = [
    { id: 'audio-to-video', name: 'Audio to Video', icon: Music, desc: 'Add visuals to your sound' },
    { id: 'video-to-audio', name: 'Extract Audio', icon: FileAudio, desc: 'Pull MP3/WAV from video' },
    { id: 'merge-audio-video', name: 'Merge Audio/Video', icon: Repeat, desc: 'Replace video sound' },
    { id: 'noise-remover', name: 'Noise Remover', icon: Wind, desc: 'Clean background noise' },
    { id: 'voice-enhancer', name: 'Voice Enhancer', icon: Mic, desc: 'Professional studio magic' },
  ];

  const videoTools = [
    { id: 'embed-comment', name: 'Embed Comment', icon: MessageSquare, desc: 'Add comments to video' },
  ];

  const ToolList = ({ tools }) => (
    <div
      className="rounded-3xl shadow-2xl border border-gray-800 p-2"
      style={{ backgroundColor: '#020617' }}
    >
      {tools.map((tool) => (
        <button
          key={tool.id}
          onClick={() => { setActiveTool(tool.id); setOpenMenu(null); setMobileOpen(false); }}
          className={`w-full p-4 rounded-2xl flex items-center gap-4 transition-all text-left mb-1 ${
            activeTool === tool.id ? 'bg-indigo-600' : 'hover:bg-gray-800'
          }`}
        >
          <div className={`p-2.5 rounded-xl ${activeTool === tool.id ? 'bg-white text-indigo-600' : 'bg-gray-900 text-gray-400'}`}>
            <tool.icon size={18} />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-sm leading-tight" style={{ color: '#FFFFFF', display: 'block' }}>
              {tool.name}
            </span>
            <span className="text-[10px] font-black uppercase tracking-[0.1em] mt-0.5" style={{ color: '#cbd5e1', display: 'block' }}>
              {tool.desc}
            </span>
          </div>
        </button>
      ))}
    </div>
  );

  const MenuButton = ({ label, menuKey }) => (
    <div className="relative" onMouseLeave={() => setOpenMenu(null)}>
      <button
        onMouseEnter={() => setOpenMenu(menuKey)}
        onClick={() => setOpenMenu(openMenu === menuKey ? null : menuKey)}
        className="flex items-center gap-2 px-6 py-2.5 rounded-2xl text-xs font-bold uppercase transition-all border bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
      >
        {label}
        <ChevronDown size={14} className={`transition-transform ${openMenu === menuKey ? 'rotate-180' : ''}`} />
      </button>

      {openMenu === menuKey && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpenMenu(null)} />
          <div className="absolute top-full left-0 w-80 z-50" style={{ marginTop: '10px' }}>
            <ToolList tools={menuKey === 'audio' ? audioTools : videoTools} />
          </div>
        </>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 shadow-sm w-full bg-white">
      <div className="max-w-6xl mx-auto py-5 px-6 flex justify-between items-center">

        {/* Logo */}
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => { setActiveTool('audio-to-video'); setMobileOpen(false); }}
        >
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
            <Sparkles size={22} className="text-white" />
          </div>
          <h1 className="text-xl font-black tracking-tighter uppercase text-gray-900">VIDABOL</h1>
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-3">
          <MenuButton label="Audio Tools" menuKey="audio" />
          <MenuButton label="Video Tools" menuKey="video" />
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} className="text-gray-800" /> : <Menu size={22} className="text-gray-800" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200" style={{ backgroundColor: '#020617' }}>
          <div className="p-3">

            {/* Audio Tools section */}
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400 px-3 py-2">
              Audio Tools
            </p>
            {audioTools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => { setActiveTool(tool.id); setMobileOpen(false); }}
                className={`w-full p-4 rounded-2xl flex items-center gap-4 transition-all text-left mb-1 ${
                  activeTool === tool.id ? 'bg-indigo-600' : 'hover:bg-gray-800'
                }`}
              >
                <div className={`p-2.5 rounded-xl ${activeTool === tool.id ? 'bg-white text-indigo-600' : 'bg-gray-900 text-gray-400'}`}>
                  <tool.icon size={18} />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-sm leading-tight" style={{ color: '#FFFFFF' }}>{tool.name}</span>
                  <span className="text-[10px] font-black uppercase tracking-[0.1em] mt-0.5" style={{ color: '#cbd5e1' }}>{tool.desc}</span>
                </div>
              </button>
            ))}

            {/* Video Tools section */}
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400 px-3 py-2 mt-2">
              Video Tools
            </p>
            {videoTools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => { setActiveTool(tool.id); setMobileOpen(false); }}
                className={`w-full p-4 rounded-2xl flex items-center gap-4 transition-all text-left mb-1 ${
                  activeTool === tool.id ? 'bg-indigo-600' : 'hover:bg-gray-800'
                }`}
              >
                <div className={`p-2.5 rounded-xl ${activeTool === tool.id ? 'bg-white text-indigo-600' : 'bg-gray-900 text-gray-400'}`}>
                  <tool.icon size={18} />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-sm leading-tight" style={{ color: '#FFFFFF' }}>{tool.name}</span>
                  <span className="text-[10px] font-black uppercase tracking-[0.1em] mt-0.5" style={{ color: '#cbd5e1' }}>{tool.desc}</span>
                </div>
              </button>
            ))}

          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;